/**
 * Integration Secrets page — admin UI for the YouTrack + Monday.com
 * credentials backing the Feature Request workflow.
 *
 * Talks to:
 *   GET    /api/feature-requests/_/integration-settings
 *   PATCH  /api/feature-requests/_/integration-settings
 *   POST   /api/feature-requests/_/integration-settings/test
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
    Save, KeyRound, ShieldCheck, Loader2, CheckCircle2, AlertTriangle,
    Plug, ExternalLink, Plus, Trash2,
} from "lucide-react";


// Canonical FR-column logical slots — exposed in the UI so admins can
// map each one to a Monday column ID. Order matters for readability;
// they mirror `build_custom_fields` in `feature_request_integrations.py`.
const MONDAY_SLOTS = [
    { key: "FEATURE_REQUEST_UUID",     label: "Feature Request UUID",      hint: "Text column — used for idempotency lookups." },
    { key: "IMPORTANCE_SCORE",         label: "Importance Score",          hint: "Numeric column (0-100)." },
    { key: "IMPORTANCE_CLASSIFICATION",label: "Importance Classification", hint: "Status/dropdown — Minimal/Low/Medium/High/Critical." },
    { key: "EFFECTIVE_PRIORITY_CLASS", label: "Effective Priority Class",  hint: "Same values as classification, but reflects mandatory overrides." },
    { key: "EVIDENCE_CONFIDENCE",      label: "Evidence Confidence",       hint: "Number 0–1." },
    { key: "CLIENT_DEMAND_SCORE",      label: "Client Demand Score",       hint: "Numeric component score." },
    { key: "OPERATIONAL_IMPACT_SCORE", label: "Operational Impact Score",  hint: "Numeric component score." },
    { key: "BUSINESS_IMPORTANCE_SCORE",label: "Business Importance Score", hint: "Numeric component score." },
    { key: "CLIENT_IMPORTANCE_SCORE",  label: "Client Importance Score",   hint: "Numeric component score." },
    { key: "REQUESTING_CLIENT_COUNT",  label: "Requesting Client Count",   hint: "Number of distinct clients asking for this." },
    { key: "MANDATORY_OVERRIDE",       label: "Mandatory Override",        hint: "Checkbox/Yes-No — is this mandatory?" },
    { key: "MANDATORY_TYPE",           label: "Mandatory Type",            hint: "Status — legal_regulatory / contractual / etc." },
    { key: "REQUEST_SOURCE",           label: "Request Source",            hint: "Status — employee / client / prospect / etc." },
    { key: "REQUESTING_DEPARTMENT",    label: "Requesting Department",     hint: "Text." },
    { key: "AI_CLARIFICATION_STATUS",  label: "AI Clarification Status",   hint: "Text — AI overall confidence." },
    { key: "YOUTRACK_ISSUE_ID",        label: "YouTrack Issue ID",         hint: "Cross-link populated after a successful YouTrack sync." },
    { key: "YOUTRACK_ISSUE_URL",       label: "YouTrack Issue URL",        hint: "Direct link to the YouTrack issue." },
];


export default function IntegrationSecretsPage() {
    const [state, setState] = useState(null);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(null);   // "youtrack" | "monday" | null
    const [testResults, setTestResults] = useState({});
    const [form, setForm] = useState({
        youtrack_base_url: "", youtrack_token: "",
        youtrack_project_id: "", youtrack_uuid_field: "",
        mock_youtrack: false,
        monday_token: "", monday_board_id: "", monday_group_id: "",
        monday_uuid_column_id: "", monday_status_column_id: "",
        mock_monday: false,
    });
    const [columnIds, setColumnIds] = useState({});   // {SLOT_KEY: column_id}
    const [customRows, setCustomRows] = useState([]); // [{key, value}]
    const [_pristine, setPristine] = useState(true);  // collapsed initially

    const refresh = async () => {
        try {
            const { data } = await api.get("/feature-requests/_/integration-settings");
            setState(data);
            setForm((prev) => ({
                ...prev,
                mock_youtrack:       !!data.youtrack?.mock,
                youtrack_base_url:   data.youtrack?.base_url || "",
                youtrack_project_id: data.youtrack?.project_id || "",
                youtrack_uuid_field: data.youtrack?.uuid_field || "External UUID",
                mock_monday:         !!data.monday?.mock,
                monday_board_id:     data.monday?.board_id || "",
                monday_group_id:     data.monday?.group_id || "",
                monday_uuid_column_id:   data.monday?.uuid_column_id || "",
                monday_status_column_id: data.monday?.status_column_id || "",
            }));
            const existing = data.monday?.column_ids || {};
            setColumnIds(existing);
            // Custom (non-canonical) rows
            const canonical = new Set(MONDAY_SLOTS.map((s) => s.key));
            const custom = Object.entries(existing)
                .filter(([k]) => !canonical.has(k))
                .map(([k, v]) => ({ key: k, value: v }));
            setCustomRows(custom);
            setPristine(true);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load settings");
        }
    };
    useEffect(() => { refresh(); }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            // Compose the column-id map (canonical + custom rows)
            const fullColumnIds = { ...columnIds };
            for (const r of customRows) {
                if (r.key && r.value) fullColumnIds[r.key.toUpperCase()] = r.value;
            }

            const body = {
                mock_youtrack:           form.mock_youtrack,
                youtrack_base_url:       form.youtrack_base_url,
                youtrack_project_id:     form.youtrack_project_id,
                youtrack_uuid_field:     form.youtrack_uuid_field,

                mock_monday:             form.mock_monday,
                monday_board_id:         form.monday_board_id,
                monday_group_id:         form.monday_group_id,
                monday_uuid_column_id:   form.monday_uuid_column_id,
                monday_status_column_id: form.monday_status_column_id,
                monday_column_ids:       fullColumnIds,
            };
            // Only include the token fields when the admin actually typed
            // something new — empty string means "no change".
            if (form.youtrack_token) body.youtrack_token = form.youtrack_token;
            if (form.monday_token)   body.monday_token   = form.monday_token;

            const { data } = await api.patch("/feature-requests/_/integration-settings", body);
            setState(data);
            // Clear the token fields after save so the masked view sticks
            setForm((prev) => ({ ...prev, youtrack_token: "", monday_token: "" }));
            toast.success("Integration settings saved.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const runTest = async (service) => {
        setTesting(service);
        try {
            const { data } = await api.post(
                "/feature-requests/_/integration-settings/test",
                { service },
            );
            setTestResults((prev) => ({ ...prev, [service]: data }));
            if (data.ok) toast.success(`${service === "youtrack" ? "YouTrack" : "Monday.com"} connection OK.`);
            else toast.error(data.detail || "Test failed");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Test failed");
        } finally {
            setTesting(null);
        }
    };

    const addCustomRow = () => setCustomRows((prev) => [...prev, { key: "", value: "" }]);
    const setCustomRow = (idx, patch) =>
        setCustomRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    const removeCustomRow = (idx) =>
        setCustomRows((prev) => prev.filter((_, i) => i !== idx));

    if (!state) {
        return (
            <div className="px-8 py-8 text-xs text-white/40 font-mono">Loading integration settings…</div>
        );
    }
    return (
        <div className="px-8 py-8 max-w-5xl" data-testid="integration-secrets-page">
            <header className="mb-8">
                <div className="label-xs text-[#7C3AED]">Feature Requests · Integrations</div>
                <h1 className="font-display text-4xl tracking-tight mt-2">Integration Secrets</h1>
                <p className="text-sm text-white/55 mt-2 max-w-2xl leading-relaxed">
                    Credentials for YouTrack &amp; Monday.com used by the Feature Request workflow.
                    Tokens are <strong>encrypted at rest</strong> and never returned to the browser —
                    you&apos;ll see a masked preview after saving. Leave the token field blank to keep
                    the existing value.
                </p>
            </header>

            <YouTrackCard
                state={state}
                form={form}
                setForm={setForm}
                onTest={() => runTest("youtrack")}
                testing={testing === "youtrack"}
                testResult={testResults.youtrack}
            />

            <MondayCard
                state={state}
                form={form}
                setForm={setForm}
                columnIds={columnIds}
                setColumnIds={setColumnIds}
                customRows={customRows}
                addCustomRow={addCustomRow}
                setCustomRow={setCustomRow}
                removeCustomRow={removeCustomRow}
                onTest={() => runTest("monday")}
                testing={testing === "monday"}
                testResult={testResults.monday}
            />

            <div className="flex items-center justify-end sticky bottom-4 z-10">
                <button onClick={handleSave} disabled={saving}
                        data-testid="save-integration-settings"
                        className="bg-white text-black px-5 py-2.5 rounded-md text-sm font-medium hover:bg-white/90 disabled:opacity-60 flex items-center gap-2 shadow-lg">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.8} />}
                    {saving ? "Saving…" : "Save settings"}
                </button>
            </div>
        </div>
    );
}


// ──────────────────────────────────────────────────────────
//  YouTrack card
// ──────────────────────────────────────────────────────────
function YouTrackCard({ state, form, setForm, onTest, testing, testResult }) {
    const mode = state?.youtrack?.mode || "mock";
    const masked = state?.youtrack?.token_masked;
    return (
        <section className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 mb-5"
                 data-testid="youtrack-card">
            <header className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <Plug className="w-5 h-5 text-[#7C3AED]" strokeWidth={1.5} />
                    <h2 className="font-display text-xl tracking-tight">YouTrack</h2>
                    <ModeBadge mode={mode} />
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={onTest} disabled={testing}
                            data-testid="test-youtrack-btn"
                            className="px-3 py-1.5 text-xs border border-white/15 hover:bg-white/5 rounded-md inline-flex items-center gap-1.5 disabled:opacity-50">
                        {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                        Test connection
                    </button>
                </div>
            </header>

            {testResult ? <TestResultBanner result={testResult} /> : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Base URL" hint="Cloud: https://YOUR-ORG.youtrack.cloud  ·  Self-host: https://yt.example.com">
                    <input value={form.youtrack_base_url}
                           onChange={(e) => setForm({ ...form, youtrack_base_url: e.target.value })}
                           placeholder="https://YOUR-ORG.youtrack.cloud"
                           data-testid="youtrack-base-url"
                           className={inputCls} />
                </Field>
                <Field label="Permanent token" hint="Profile → Account Security → New token… Issue Create/Update scopes.">
                    <input type="password" value={form.youtrack_token}
                           onChange={(e) => setForm({ ...form, youtrack_token: e.target.value })}
                           placeholder={masked || "perm:..."}
                           autoComplete="new-password"
                           data-testid="youtrack-token"
                           className={inputCls} />
                </Field>
                <Field label="Project ID" hint="Short name like DEMO, or internal id like 0-3 (from project URL).">
                    <input value={form.youtrack_project_id}
                           onChange={(e) => setForm({ ...form, youtrack_project_id: e.target.value })}
                           placeholder="0-3 or DEMO"
                           data-testid="youtrack-project-id"
                           className={inputCls} />
                </Field>
                <Field label="External UUID custom field" hint="Default: 'External UUID'. Must exist as a string custom field on the project.">
                    <input value={form.youtrack_uuid_field}
                           onChange={(e) => setForm({ ...form, youtrack_uuid_field: e.target.value })}
                           placeholder="External UUID"
                           data-testid="youtrack-uuid-field"
                           className={inputCls} />
                </Field>
            </div>

            <label className="mt-4 flex items-center gap-2 text-xs text-white/60">
                <input type="checkbox" checked={form.mock_youtrack}
                       onChange={(e) => setForm({ ...form, mock_youtrack: e.target.checked })}
                       data-testid="youtrack-mock-toggle" />
                Force mock mode (no live API calls — useful for QA)
            </label>
        </section>
    );
}


// ──────────────────────────────────────────────────────────
//  Monday card
// ──────────────────────────────────────────────────────────
function MondayCard({
    state, form, setForm, columnIds, setColumnIds,
    customRows, addCustomRow, setCustomRow, removeCustomRow,
    onTest, testing, testResult,
}) {
    const mode = state?.monday?.mode || "mock";
    const masked = state?.monday?.token_masked;
    const setSlot = (slot, value) =>
        setColumnIds((prev) => {
            const next = { ...prev };
            if (value) next[slot] = value;
            else delete next[slot];
            return next;
        });

    return (
        <section className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 mb-5"
                 data-testid="monday-card">
            <header className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-[#7C3AED]" strokeWidth={1.5} />
                    <h2 className="font-display text-xl tracking-tight">Monday.com</h2>
                    <ModeBadge mode={mode} />
                </div>
                <div className="flex items-center gap-2">
                    <a href="https://developer.monday.com/api-reference/docs/authentication"
                       target="_blank" rel="noopener noreferrer"
                       className="text-[11px] text-violet-200 hover:text-violet-100 inline-flex items-center gap-1">
                        Auth docs <ExternalLink className="w-3 h-3" />
                    </a>
                    <button onClick={onTest} disabled={testing}
                            data-testid="test-monday-btn"
                            className="px-3 py-1.5 text-xs border border-white/15 hover:bg-white/5 rounded-md inline-flex items-center gap-1.5 disabled:opacity-50">
                        {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                        Test connection
                    </button>
                </div>
            </header>

            {testResult ? <TestResultBanner result={testResult} /> : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="API token (v2)" hint="Avatar → Developers → My Access Tokens.">
                    <input type="password" value={form.monday_token}
                           onChange={(e) => setForm({ ...form, monday_token: e.target.value })}
                           placeholder={masked || "eyJ..."}
                           autoComplete="new-password"
                           data-testid="monday-token"
                           className={inputCls} />
                </Field>
                <Field label="Board ID" hint="From URL monday.com/boards/<BOARD_ID>.">
                    <input value={form.monday_board_id}
                           onChange={(e) => setForm({ ...form, monday_board_id: e.target.value })}
                           placeholder="123456789"
                           data-testid="monday-board-id"
                           className={inputCls} />
                </Field>
                <Field label="Group ID (optional)" hint="Which section of the board to drop new items into.">
                    <input value={form.monday_group_id}
                           onChange={(e) => setForm({ ...form, monday_group_id: e.target.value })}
                           placeholder="topics"
                           data-testid="monday-group-id"
                           className={inputCls} />
                </Field>
                <Field label="Status column ID" hint="Required for the daily YouTrack → Monday status sync.">
                    <input value={form.monday_status_column_id}
                           onChange={(e) => setForm({ ...form, monday_status_column_id: e.target.value })}
                           placeholder="status"
                           data-testid="monday-status-col"
                           className={inputCls} />
                </Field>
            </div>

            <label className="mt-4 flex items-center gap-2 text-xs text-white/60">
                <input type="checkbox" checked={form.mock_monday}
                       onChange={(e) => setForm({ ...form, mock_monday: e.target.checked })}
                       data-testid="monday-mock-toggle" />
                Force mock mode (no live GraphQL calls — useful for QA)
            </label>

            {/* ── Column-ID mapping ─────────────────────────── */}
            <div className="mt-6 border-t border-white/10 pt-5">
                <h3 className="font-display text-lg mb-1">Column ID mapping</h3>
                <p className="text-[11px] text-white/45 mb-4 leading-relaxed">
                    Map each feature-request slot to a Monday column ID. Missing entries are
                    skipped silently — Monday won&apos;t accept a write to a non-existent column.
                    Get column IDs from the column&apos;s ⋮ menu → <em>Customize column</em> → settings.
                </p>
                <div className="grid grid-cols-1 gap-2.5">
                    {MONDAY_SLOTS.map((slot) => (
                        <div key={slot.key} className="grid grid-cols-12 gap-3 items-center"
                             data-testid={`monday-slot-${slot.key}`}>
                            <div className="col-span-12 sm:col-span-5">
                                <div className="text-xs">{slot.label}</div>
                                <div className="text-[10px] text-white/40 font-mono">{slot.key}</div>
                            </div>
                            <div className="col-span-12 sm:col-span-7">
                                <input value={columnIds[slot.key] || ""}
                                       onChange={(e) => setSlot(slot.key, e.target.value.trim())}
                                       placeholder="e.g. text_mkkex8"
                                       className={inputCls + " text-[12px]"}
                                       data-testid={`monday-slot-input-${slot.key}`} />
                                <div className="text-[10px] text-white/35 mt-1">{slot.hint}</div>
                            </div>
                        </div>
                    ))}

                    {customRows.length > 0 ? (
                        <div className="text-[10px] uppercase tracking-wider text-white/35 mt-4">
                            Custom slots
                        </div>
                    ) : null}
                    {customRows.map((row, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-3 items-center"
                             data-testid={`monday-custom-row-${idx}`}>
                            <div className="col-span-12 sm:col-span-5">
                                <input value={row.key}
                                       onChange={(e) => setCustomRow(idx, { key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_") })}
                                       placeholder="SLOT_KEY"
                                       className={inputCls + " text-[12px] font-mono"}
                                       data-testid={`monday-custom-key-${idx}`} />
                            </div>
                            <div className="col-span-10 sm:col-span-6">
                                <input value={row.value}
                                       onChange={(e) => setCustomRow(idx, { value: e.target.value.trim() })}
                                       placeholder="column_id"
                                       className={inputCls + " text-[12px]"}
                                       data-testid={`monday-custom-value-${idx}`} />
                            </div>
                            <button onClick={() => removeCustomRow(idx)}
                                    className="col-span-2 sm:col-span-1 inline-flex items-center justify-center text-rose-300 hover:bg-rose-400/10 rounded p-2"
                                    title="Remove">
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
                <button onClick={addCustomRow}
                        className="mt-4 px-3 py-1.5 text-xs border border-white/15 hover:bg-white/5 rounded-md inline-flex items-center gap-1.5"
                        data-testid="monday-add-custom-row">
                    <Plus className="w-3.5 h-3.5" /> Add custom slot
                </button>
            </div>
        </section>
    );
}


// ──────────────────────────────────────────────────────────
//  UI primitives
// ──────────────────────────────────────────────────────────
const inputCls = "w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30 focus:ring-2 focus:ring-white/10";


function Field({ label, hint, children }) {
    return (
        <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-white/55 block mb-1.5">{label}</span>
            {children}
            {hint ? <div className="text-[10px] text-white/40 font-mono mt-1.5">{hint}</div> : null}
        </label>
    );
}


function ModeBadge({ mode }) {
    const live = mode === "live";
    return (
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md ${
            live ? "bg-emerald-300/15 text-emerald-200 border border-emerald-300/40"
                : "bg-amber-300/10 text-amber-200 border border-amber-300/40"
        }`} data-testid={`mode-badge-${mode}`}>
            {live ? "Live" : "Mock"}
        </span>
    );
}


function TestResultBanner({ result }) {
    const ok = !!result?.ok;
    return (
        <div className={`mb-4 px-3 py-2 rounded-md text-xs inline-flex items-center gap-2 ${
            ok ? "bg-emerald-300/[0.07] border border-emerald-300/30 text-emerald-100"
               : "bg-rose-400/[0.07] border border-rose-400/30 text-rose-100"
        }`} data-testid="test-result-banner">
            {ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            <span>{result?.detail || (ok ? "Connection OK." : "Connection failed.")}</span>
        </div>
    );
}
